import type { CanvasAxis, ZoomMode } from '../Dashboard/viewDescriptors'
import type { PannableCanvasTransform } from './usePannableCanvasState'

const DEFAULT_TRANSFORM: PannableCanvasTransform = { x: 0, y: 0, zoom: 1 }
const DEFAULT_MIN_ZOOM = 0.01
const DEFAULT_MAX_ZOOM = 4
export const MIN_LEGIBLE_ZOOM = 0.85

export type ContentBounds = {
  left: number
  top: number
  right: number
  bottom: number
  width: number
  height: number
  centerX: number
  centerY: number
}

export type FitToContentOptions = {
  padding?: number
  minZoom?: number
  maxZoom?: number
  zoomAxis?: CanvasAxis
  zoomMode?: ZoomMode
}

const finite = (value: number): boolean => Number.isFinite(value)

export const calculateResponsivePadding = (viewportWidth: number): number => {
  return Math.min(64, Math.max(8, Math.round(viewportWidth * 0.04)))
}

export type EdgeFades = {
  top: boolean
  bottom: boolean
  left: boolean
  right: boolean
}

export const computeEdgeFades = (
  transform: PannableCanvasTransform,
  bounds: ContentBounds | null,
  viewport: { width: number; height: number } | null,
  zoomAxis: CanvasAxis = 'xy',
  zoomMode?: ZoomMode,
): EdgeFades => {
  if (!bounds || !viewport || viewport.width <= 0 || viewport.height <= 0) {
    return { top: false, bottom: false, left: false, right: false }
  }
  const isSemantic = zoomMode === 'semantic'
  const scaleX = isSemantic || zoomAxis === 'y' || zoomAxis === 'none' ? 1 : transform.zoom
  const scaleY = isSemantic || zoomAxis === 'x' || zoomAxis === 'none' ? 1 : transform.zoom

  const contentLeft = transform.x + bounds.left * scaleX
  const contentRight = transform.x + bounds.right * scaleX
  const contentTop = transform.y + bounds.top * scaleY
  const contentBottom = transform.y + bounds.bottom * scaleY

  return {
    top: contentTop < -1,
    bottom: contentBottom > viewport.height + 1,
    left: contentLeft < -1,
    right: contentRight > viewport.width + 1,
  }
}

export const scaleFromTransform = (transform: string): { scaleX: number; scaleY: number } => {
  if (!transform || transform === 'none') return { scaleX: 1, scaleY: 1 }
  const matrix = transform.match(/^matrix\(([^,]+),\s*[^,]+,\s*[^,]+,\s*([^,]+),/)
  const matrix3d = transform.match(/^matrix3d\(([^)]+)\)/)
  const scaleFn = transform.match(/scale(?:3d)?\(([^)]+)\)/)
  const parts = matrix3d?.[1]?.split(',').map((part) => Number(part.trim()))
  const scaleArgs = scaleFn?.[1]?.split(',').map((part) => Number(part.trim()))
  const rawScaleX = Math.abs(
    matrix ? Number(matrix[1])
      : parts ? parts[0]
        : scaleArgs ? scaleArgs[0]
          : 1,
  )
  const rawScaleY = Math.abs(
    matrix ? Number(matrix[2])
      : parts ? parts[5]
        : scaleArgs ? (scaleArgs[1] ?? scaleArgs[0])
          : 1,
  )
  const scaleX = finite(rawScaleX) && rawScaleX > 0 ? rawScaleX : 1
  const scaleY = finite(rawScaleY) && rawScaleY > 0 ? rawScaleY : 1
  return { scaleX, scaleY }
}

export const measureLayerContentBounds = (layerEl: HTMLElement): ContentBounds | null => {
  const layerRect = layerEl.getBoundingClientRect()
  if (layerRect.width <= 0 && layerRect.height <= 0) return null
  const { scaleX, scaleY } = scaleFromTransform(window.getComputedStyle(layerEl).transform)
  const elements = [layerEl, ...Array.from(layerEl.querySelectorAll<HTMLElement>('*'))]
  let left = Infinity
  let top = Infinity
  let right = -Infinity
  let bottom = -Infinity

  for (const element of elements) {
    if (element !== layerEl) {
      const style = window.getComputedStyle(element)
      if (
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        style.visibility === 'collapse'
      ) {
        continue
      }
      if (element.closest('[data-canvas-overlay-anchor]')) {
        continue
      }
    }

    const rect = element.getBoundingClientRect()
    if (!finite(rect.left) || !finite(rect.top) || !finite(rect.right) || !finite(rect.bottom)) continue
    if (rect.width <= 0 && rect.height <= 0) continue

    let clippedLeft = rect.left
    let clippedTop = rect.top
    let clippedRight = rect.right
    let clippedBottom = rect.bottom

    // Intersect with clipping ancestors up to layerEl to ignore off-window clipped elements (DEF-02)
    let parent = element.parentElement
    let isCompletelyClipped = false

    while (parent && parent !== layerEl.parentElement) {
      if (parent === layerEl) {
        parent = parent.parentElement
        continue
      }
      const parentStyle = window.getComputedStyle(parent)
      const overflowX = parentStyle.overflowX
      const overflowY = parentStyle.overflowY
      const clipsX = overflowX === 'hidden' || overflowX === 'clip' || overflowX === 'scroll' || overflowX === 'auto'
      const clipsY = overflowY === 'hidden' || overflowY === 'clip' || overflowY === 'scroll' || overflowY === 'auto'

      if (clipsX || clipsY) {
        const parentRect = parent.getBoundingClientRect()
        if (clipsX) {
          clippedLeft = Math.max(clippedLeft, parentRect.left)
          clippedRight = Math.min(clippedRight, parentRect.right)
        }
        if (clipsY) {
          clippedTop = Math.max(clippedTop, parentRect.top)
          clippedBottom = Math.min(clippedBottom, parentRect.bottom)
        }
        if (clippedRight <= clippedLeft || clippedBottom <= clippedTop) {
          isCompletelyClipped = true
          break
        }
      }
      parent = parent.parentElement
    }

    if (isCompletelyClipped) continue

    const localLeft = (clippedLeft - layerRect.left) / scaleX
    const localTop = (clippedTop - layerRect.top) / scaleY
    const localRight = (clippedRight - layerRect.left) / scaleX
    const localBottom = (clippedBottom - layerRect.top) / scaleY

    left = Math.min(left, localLeft)
    top = Math.min(top, localTop)
    right = Math.max(right, localRight)
    bottom = Math.max(bottom, localBottom)
  }

  if (!finite(left) || !finite(top) || !finite(right) || !finite(bottom) || right <= left || bottom <= top) {
    return null
  }

  return {
    left,
    top,
    right,
    bottom,
    width: right - left,
    height: bottom - top,
    centerX: (left + right) / 2,
    centerY: (top + bottom) / 2,
  }
}

export const calculateFitTransform = (
  bounds: ContentBounds,
  viewportRect: { width: number; height: number },
  opts?: FitToContentOptions,
): PannableCanvasTransform => {
  if (viewportRect.width <= 0 || viewportRect.height <= 0) {
    return DEFAULT_TRANSFORM
  }
  const padding = opts?.padding ?? calculateResponsivePadding(viewportRect.width)
  const isSemantic = opts?.zoomMode === 'semantic'
  const minFloor = isSemantic ? MIN_LEGIBLE_ZOOM : DEFAULT_MIN_ZOOM
  const minZoom = Math.max(opts?.minZoom ?? minFloor, isSemantic ? MIN_LEGIBLE_ZOOM : 0)
  const maxZoom = opts?.maxZoom ?? DEFAULT_MAX_ZOOM
  const availableWidth = Math.max(1, viewportRect.width - padding * 2)
  const availableHeight = Math.max(1, viewportRect.height - padding * 2)
  const zoomAxis = opts?.zoomAxis ?? 'xy'
  const safeBoundsWidth = Math.max(1, bounds.width)
  const safeBoundsHeight = Math.max(1, bounds.height)

  if (zoomAxis === 'none') {
    const x = bounds.width <= availableWidth
      ? padding + (availableWidth - bounds.width) / 2 - bounds.left
      : padding - bounds.left
    const y = bounds.height <= availableHeight
      ? padding + (availableHeight - bounds.height) / 2 - bounds.top
      : padding - bounds.top
    return { x, y, zoom: 1 }
  }

  let rawZoom: number
  if (zoomAxis === 'x') {
    // For 'x' views, fit height to keep row heights native/readable, leaving horizontal to panning
    const heightFit = availableHeight / safeBoundsHeight
    rawZoom = Math.min(1, heightFit)
  } else if (zoomAxis === 'y') {
    // For 'y' views, fit width, leaving vertical to panning
    const widthFit = availableWidth / safeBoundsWidth
    rawZoom = Math.min(1, widthFit)
  } else {
    // For 'xy' spatial views, fit both dimensions
    rawZoom = Math.min(availableWidth / safeBoundsWidth, availableHeight / safeBoundsHeight)
  }

  const zoom = Math.min(maxZoom, Math.max(minZoom, rawZoom))
  // Semantic views render at zoom: 1 on the canvas layer; fit calculations must use the rendered scale
  const effectiveZoom = isSemantic ? 1 : zoom

  const scaleX = isSemantic || zoomAxis === 'y' ? 1 : effectiveZoom
  const scaleY = isSemantic || zoomAxis === 'x' ? 1 : effectiveZoom

  let x: number
  if (bounds.width * scaleX <= availableWidth) {
    x = padding + (availableWidth - bounds.width * scaleX) / 2 - bounds.left * scaleX
  } else {
    // When content overflows horizontally, align left edge to padding so timeline start is visible
    x = padding - bounds.left * scaleX
  }

  let y: number
  if (bounds.height * scaleY <= availableHeight) {
    y = padding + (availableHeight - bounds.height * scaleY) / 2 - bounds.top * scaleY
  } else {
    // When content overflows vertically, align top edge to padding
    y = padding - bounds.top * scaleY
  }

  return { x, y, zoom: effectiveZoom }
}

export const fitToContent = (
  layerEl: HTMLElement,
  viewportRect: DOMRect,
  opts?: FitToContentOptions,
): PannableCanvasTransform => {
  if (viewportRect.width <= 0 || viewportRect.height <= 0) {
    return DEFAULT_TRANSFORM
  }
  const bounds = measureLayerContentBounds(layerEl)
  if (!bounds) return DEFAULT_TRANSFORM
  return calculateFitTransform(bounds, viewportRect, opts)
}
